import { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import UploadComponent from './UploadComponent'
import type { FileItem } from './UploadComponent'
import { COLORS } from '../theme'
import { stepHeadingSx, stepDescriptionSx, warningBoxSx } from '../styles'

const MAX_FILES = Number(import.meta.env.VITE_MAX_FILES_PER_UPLOAD) || 15

interface DocItem {
  id: number
  doc_url: string
}

interface UploadStepProps {
  title: string
  description: string
  descriptionVariant?: 'body1' | 'body2'
  skipBanner?: string
  skipHintBottom?: string
  docs: DocItem[] | undefined
  onUploadFiles: (files: File[]) => Promise<Array<{ id: string }>>
  onDeleteFile: (docId: string) => Promise<void>
  onUploadUrl?: (url: string) => Promise<{ id?: string }>
  onUploadingChange?: (isUploading: boolean) => void
}

export default function UploadStep({
  title,
  description,
  descriptionVariant = 'body1',
  skipBanner,
  skipHintBottom,
  docs,
  onUploadFiles,
  onDeleteFile,
  onUploadUrl,
  onUploadingChange,
}: UploadStepProps) {
  const existingFiles = useMemo<FileItem[]>(() => {
    if (!docs) return []
    return docs.map((doc) => ({
      uid: `server-${doc.id}`,
      name: doc.doc_url.replace(/^.*?(\d{8}_\d{6}_)/, '').replace(/_/g, ' '),
      status: 'success' as const,
      type: 'file' as const,
      docId: String(doc.id),
    }))
  }, [docs])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="h5" sx={stepHeadingSx}>
        {title}
      </Typography>
      <Typography variant={descriptionVariant} sx={stepDescriptionSx}>
        {description}
      </Typography>
      {skipBanner && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            ...warningBoxSx,
            mb: 2,
          }}
        >
          <InfoOutlinedIcon
            sx={{
              color: COLORS.warningIcon,
              fontSize: 22,
              mt: 0.25,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: COLORS.labelText, lineHeight: 1.6 }}
          >
            {skipBanner}
          </Typography>
        </Box>
      )}

      <Box
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <UploadComponent
          onUploadFiles={onUploadFiles}
          onDeleteFile={onDeleteFile}
          onUploadUrl={onUploadUrl}
          onUploadingChange={onUploadingChange}
          existingFiles={existingFiles}
          maxFiles={MAX_FILES}
          maxSize={26214400}
          fillHeight
        />
      </Box>
      {skipHintBottom && (
        <Typography
          variant="body2"
          sx={{ color: COLORS.muted, mt: 2, textAlign: 'center' }}
        >
          {skipHintBottom}
        </Typography>
      )}
    </Box>
  )
}
